/**
 * Beispielhafte Verkaufsreihenfolge Tab für Schritt 3
 * Zeigt detaillierte, beispielhafte Verkaufsreihenfolge gruppiert nach Portfolio
 */

import React, { useMemo } from 'react';
import {
  Header,
  SpaceBetween,
  Box,
  Container,
  ColumnLayout,
  Alert,
  ExpandableSection
} from '@cloudscape-design/components';

import {
  PortfolioPosition,
  EntnahmeSimulation,
  SteuerParameter,
  SteuerKomponentenAnzeige
} from '@/types';
import { formatCurrency } from '@/utils/formatters';
import { PortfolioService } from '@/services/portfolioService';
import { SPARERFREIBETRAEGE } from '@/utils/constants';
import { berechneNettoKapitalsteuerNachVerrechnung, portfolioPositionKey } from '@/utils/capitalGainsAggregation';

interface VerkaufsreihenfolgeTabProps {
  portfolioPositionen: PortfolioPosition[];
  entnahmeSimulation: EntnahmeSimulation | null;
  steuerParameter: SteuerParameter | null;
  sparerPauschbetragEnabled?: boolean;
}

/**
 * Komponente für die Anzeige der beispielhaften Verkaufsreihenfolge
 */
const VerkaufsanweisungenTab: React.FC<VerkaufsreihenfolgeTabProps> = ({
  portfolioPositionen,
  steuerParameter,
  sparerPauschbetragEnabled = false
}) => {
  
  // Sparer-Pauschbetrag Betrag aus Berechnungsparametern ermitteln
  const sparerPauschbetragBetrag = steuerParameter 
    ? SPARERFREIBETRAEGE[steuerParameter.familienstand]
    : SPARERFREIBETRAEGE.alleinstehend;

  // Nur Positionen mit Verkauf > 0% anzeigen, sortiert für konsistente Sparer-Pauschbetrag Anwendung
  const verkaufsPositionen = useMemo(() => {
    return portfolioPositionen
      .filter(pos => !pos.exclude && pos.sellPercentage > 0)
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [portfolioPositionen]);

  // Beispielhafte Verkaufsreihenfolge nach Portfolio gruppieren; Steuer wie TabellenAnsicht (aggregiert)
  const verkaufsanweisungenNachPortfolio = useMemo(() => {
    const nachPortfolio = new Map<string, any[]>();
    const allowanceTrackingPerPosition: Array<{ positionName: string; verwendeter: number; verbleibend: number }> =
      [];

    const fifoByKey = new Map<
      string,
      {
        fifoDetails: ReturnType<typeof PortfolioService.calculateFIFOTaxableProfitWithDetails>;
        fifoGewinn: number;
      }
    >();

    verkaufsPositionen.forEach((position) => {
      const fifoDetails = PortfolioService.calculateFIFOTaxableProfitWithDetails(position, position.sellPercentage);
      const fifoGewinn = fifoDetails.soldLots.reduce(
        (sum, lot) => sum + PortfolioService.applyTaxTreatment(lot.profit, lot.effectiveTaxTreatment),
        0
      );
      fifoByKey.set(portfolioPositionKey(position), { fifoDetails, fifoGewinn });
    });

    const rows = verkaufsPositionen.map((position) => ({
      positionKey: portfolioPositionKey(position),
      gewinn: fifoByKey.get(portfolioPositionKey(position))!.fifoGewinn
    }));

    const net =
      rows.length > 0
        ? berechneNettoKapitalsteuerNachVerrechnung(
            rows,
            steuerParameter,
            sparerPauschbetragEnabled,
            sparerPauschbetragBetrag
          )
        : null;

    let cumulativePauschRemaining = sparerPauschbetragBetrag;

    verkaufsPositionen.forEach((position) => {
      const portfolio = position.portfolio || 'Unbekanntes Portfolio';

      if (!nachPortfolio.has(portfolio)) {
        nachPortfolio.set(portfolio, []);
      }

      const verkaufsAnteile = position.quantity * (position.sellPercentage / 100);
      const verkaufsWert = verkaufsAnteile * position.currentPrice;

      const key = portfolioPositionKey(position);
      const { fifoDetails, fifoGewinn } = fifoByKey.get(key)!;

      const verwendeterPauschbetrag = net?.verwendeterPauschbetragProPosition.get(key) ?? 0;
      const kapitalertragsteuer = net?.kapitalertragsteuerProPosition.get(key) ?? 0;
      const solidaritaetszuschlag = net?.solidaritaetszuschlagProPosition.get(key) ?? 0;
      const kirchensteuer = net?.kirchensteuerProPosition.get(key) ?? 0;
      const gesamtsteuer = net?.gesamtsteuerProPosition.get(key) ?? 0;
      const allocatedGewinn = net?.steuerpflichtigerGewinnNachPauschProPosition.get(key) ?? 0;

      // Gleicher KiSt-Schlüssel wie berechneNettoKapitalsteuerNachVerrechnung; Steuerkomponenten nur aus Aggregation
      const steuerInfo = {
        verkaufsQuantity: verkaufsAnteile,
        verkaufsWert,
        gewinn: allocatedGewinn,
        bruttoGewinn: fifoDetails.totalProfit,
        fifoGewinnVerrechnet: fifoGewinn,
        kapitalertragsteuer,
        solidaritaetszuschlag,
        kirchensteuer,
        gesamtsteuer,
        nettoErloes: verkaufsWert - gesamtsteuer
      };

      const nettoErloes = verkaufsWert - gesamtsteuer;

      if (sparerPauschbetragEnabled) {
        cumulativePauschRemaining -= verwendeterPauschbetrag;
        allowanceTrackingPerPosition.push({
          positionName: position.name,
          verwendeter: verwendeterPauschbetrag,
          verbleibend: Math.max(0, cumulativePauschRemaining)
        });
      }

      nachPortfolio.get(portfolio)!.push({
        ...position,
        verkaufsAnteile,
        verkaufsWert,
        fifoDetails,
        steuerInfo,
        verwendeterPauschbetrag,
        nettoErloes
      });
    });

    return { nachPortfolio, allowanceTracking: allowanceTrackingPerPosition, kapitalNetto: net };
  }, [verkaufsPositionen, steuerParameter, sparerPauschbetragEnabled, sparerPauschbetragBetrag]);

  const gesamtStatistiken = useMemo(() => {
    const net = verkaufsanweisungenNachPortfolio.kapitalNetto;
    const gesamtVerkaufsWert = verkaufsPositionen.reduce(
      (s, p) => s + p.quantity * (p.sellPercentage / 100) * p.currentPrice,
      0
    );
    if (!net) {
      return {
        gesamtVerkaufsWert,
        gesamtSteuer: 0,
        gesamtNettoErloes: gesamtVerkaufsWert,
        gesamtVerwendeterPauschbetrag: 0,
        gesamtSteuerpflichtigerGewinn: 0
      };
    }
    return {
      gesamtVerkaufsWert,
      gesamtSteuer: net.gesamtsteuer,
      gesamtNettoErloes: gesamtVerkaufsWert - net.gesamtsteuer,
      gesamtVerwendeterPauschbetrag: net.verwendeterPauschbetrag,
      gesamtSteuerpflichtigerGewinn: net.steuerpflichtigerGewinnNachPausch
    };
  }, [verkaufsanweisungenNachPortfolio, verkaufsPositionen]);

  // Hilfsfunktion zum Formatieren von Steuerdetails
  const formatTaxDetails = (steuerInfo: SteuerKomponentenAnzeige, currency: string) => {
    const parts = [];
    parts.push(`KapESt: ${formatCurrency(steuerInfo.kapitalertragsteuer, currency)}`);
    parts.push(`Soli: ${formatCurrency(steuerInfo.solidaritaetszuschlag, currency)}`);
    if (steuerInfo.kirchensteuer > 0) {
      parts.push(`KirchSt: ${formatCurrency(steuerInfo.kirchensteuer, currency)}`);
    }
    return parts.join(' + ');
  };

  if (verkaufsPositionen.length === 0) {
    return (
      <Alert type="info">
        <strong>Keine beispielhafte Verkaufsreihenfolge</strong>
        <br />
                  Geben Sie zunächst in der &quot;Beispielhaften Simulation von Verkaufs-Szenarien&quot; Verkauf %-Werte ein, um eine beispielhafte Verkaufsreihenfolge zu generieren.
      </Alert>
    );
  }

  return (
    <SpaceBetween direction="vertical" size="l">
      {/* Saubere Einführung */}
      <Container
        header={
          <Header
            variant="h2"
            description="Diese Übersicht zeigt beispielhaft eine mögliche Reihenfolge, wie Verkäufe in der Modellrechnung angeordnet wurden (FIFO-Regeln, Teilfreistellungen, aktuelle Kurse). Sie dient ausschließlich der Veranschaulichung und ist keine Empfehlung zur tatsächlichen Durchführung. Für verbindliche Entscheidungen wenden Sie sich bitte an eine Steuerberaterin oder einen Steuerberater."
          >
            Ihre beispielhafte Verkaufsreihenfolge
          </Header>
        }
      >
        <SpaceBetween direction="vertical" size="m">


        </SpaceBetween>
      </Container>

      {/* Portfolio-Entnahme with consistent KPI styling */}
      <Container
        header={
          <Header 
            variant="h3"
            description="Beispielhafte Portfolio-Entnahme im Modell"
          >
            Portfolio-Entnahme
          </Header>
        }
      >
        <SpaceBetween direction="vertical" size="m">
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
            {/* Brutto-Verkaufserlös */}
            <div style={{ 
              padding: '16px', 
              border: '1px solid #d5dbdb', 
              borderRadius: '8px',
              backgroundColor: '#fafbfc'
            }}>
              <Box variant="awsui-key-label">Brutto-Verkaufserlös</Box>
              <Box variant="awsui-value-large">
                {formatCurrency(gesamtStatistiken.gesamtVerkaufsWert)}
              </Box>
              <Box variant="small" color="text-body-secondary">
                Vor Steuern
              </Box>
            </div>

            {/* Steuerpflichtiger Gewinn */}
            <div style={{ 
              padding: '16px', 
              border: '1px solid #d5dbdb', 
              borderRadius: '8px',
              backgroundColor: '#fafbfc'
            }}>
              <Box variant="awsui-key-label">Steuerpflichtiger Gewinn</Box>
              <Box variant="awsui-value-large" color="text-status-warning">
                {formatCurrency(gesamtStatistiken.gesamtSteuerpflichtigerGewinn)}
              </Box>
              <Box variant="small" color="text-body-secondary">
                Nach FIFO + Teilfreistellungen
              </Box>
            </div>

            {/* Sparer-Pauschbetrag (nur wenn aktiviert und verwendet) */}
            {sparerPauschbetragEnabled && gesamtStatistiken.gesamtVerwendeterPauschbetrag > 0 && (
              <div style={{ 
                padding: '16px', 
                border: '1px solid #d5dbdb', 
                borderRadius: '8px',
                backgroundColor: '#fafbfc'
              }}>
                <Box variant="awsui-key-label">Sparer-Pauschbetrag</Box>
                <Box variant="awsui-value-large" color="text-status-success">
                  -{formatCurrency(gesamtStatistiken.gesamtVerwendeterPauschbetrag)}
                </Box>
                <Box variant="small" color="text-body-secondary">
                  von {formatCurrency(sparerPauschbetragBetrag)} verwendet
                </Box>
              </div>
            )}

            {/* Gesamte Steuerlast */}
            <div style={{ 
              padding: '16px', 
              border: '1px solid #d5dbdb', 
              borderRadius: '8px',
              backgroundColor: '#fafbfc'
            }}>
              <Box variant="awsui-key-label">Gesamte Steuerlast</Box>
              <Box variant="awsui-value-large" color="text-status-error">
                {formatCurrency(gesamtStatistiken.gesamtSteuer)}
              </Box>
              <Box variant="small" color="text-body-secondary">
                {(() => {
                  // Show detailed breakdown based on church tax setting
                  if (steuerParameter?.kirchensteuer === 'baden-wuerttemberg-bayern') {
                    return 'KapESt (24.51%) + SolZ (1.35%) + KiSt (1.96%)';
                  } else if (steuerParameter?.kirchensteuer === 'andere-bundeslaender') {
                    return 'KapESt (24.45%) + SolZ (1.34%) + KiSt (2.20%)';
                  } else {
                    return 'KapESt (25.00%) + SolZ (1.38%) + KiSt (0.00%)';
                  }
                })()}
              </Box>
            </div>

            {/* Netto-Erlös */}
            <div style={{ 
              padding: '16px', 
              border: '1px solid #d5dbdb', 
              borderRadius: '8px',
              backgroundColor: '#fafbfc'
            }}>
              <Box variant="awsui-key-label">Netto-Erlös</Box>
              <Box variant="awsui-value-large" color="text-status-success">
                {formatCurrency(gesamtStatistiken.gesamtNettoErloes)}
              </Box>
              <Box variant="small" color="text-body-secondary">
                Nach Steuern
              </Box>
            </div>
          </div>
        </SpaceBetween>
      </Container>

      {/* Aufgaben nach Portfolio */}
      {Array.from(verkaufsanweisungenNachPortfolio.nachPortfolio.entries()).map(([portfolio, positions]) => (
          <Container
          key={portfolio}
          header={
            <Header 
              variant="h3"
              counter={`${positions.length} Aufgabe${positions.length > 1 ? 'n' : ''}`}
            >
              {portfolio}
            </Header>
          }
        >
          <SpaceBetween direction="vertical" size="l">
            {positions.map((position, index) => (
              <Container
                key={position.identifier}
                header={
                  <Header
                    variant="h3"
                  >
                    {index + 1}. {position.name}
                  </Header>
                }
              >
                <SpaceBetween direction="vertical" size="m">
                  {/* Hauptauftrag */}
                  <Box variant="h4" color="text-status-info">
                    Beispiel: Verkauf von {position.verkaufsAnteile.toFixed(4)} Anteilen für {formatCurrency(position.verkaufsWert, position.currency)} im Modell
                  </Box>
                  
                  <Box variant="small" color="text-body-secondary">
                    ISIN: {position.identifier} • {position.sellPercentage.toFixed(1)}% Ihrer Position
                  </Box>

                  {/* Finanzübersicht */}
                  <ColumnLayout columns={3}>
                    <div>
                      <Box variant="small" color="text-body-secondary">Steuerpflichtiger Gewinn</Box>
                      <Box variant="h3" color="text-status-warning">
                        {formatCurrency(position.steuerInfo.gewinn, position.currency)}
                      </Box>
                      <Box variant="small" color="text-body-secondary">
                        Anteil nach positionsübergreifender Verrechnung und ggf. Sparer-Pauschbetrag (wie Tabellenansicht).
                      </Box>
                      {sparerPauschbetragEnabled && position.verwendeterPauschbetrag > 0 && (
                        <Box variant="small" color="text-status-success">
                          ✓ Pauschbetrag: -{formatCurrency(position.verwendeterPauschbetrag, position.currency)}
                        </Box>
                      )}
                    </div>

                    <div>
                      <Box variant="small" color="text-body-secondary">Steuern</Box>
                      <Box variant="h3" color="text-status-error">
                        {formatCurrency(position.steuerInfo.gesamtsteuer, position.currency)}
                      </Box>
                      <Box variant="small" color="text-body-secondary">
                        {formatTaxDetails(position.steuerInfo, position.currency)}
                      </Box>
                      <Box variant="small" color="text-body-secondary" padding={{ top: 'xxs' }}>
                        KapESt/Soli/KiSt wie in Schritt 1; Beträge sind auf alle Verkäufe verteilt (nicht die Steuer nur für diese Position isoliert).
                      </Box>
                    </div>

                    <div>
                      <Box variant="small" color="text-body-secondary">Sie erhalten</Box>
                      <Box variant="h3" color="text-status-success">
                        {formatCurrency(position.nettoErloes, position.currency)}
                      </Box>
                      <Box variant="small" color="text-body-secondary">
                        Nach Abzug aller Steuern
                      </Box>
                    </div>
                  </ColumnLayout>

                  {/* Kompakte Steuerberechnung */}
                  <ExpandableSection
                    headerText={`Steuerberechnung Details${position.fifoDetails.soldLots.length > 1 ? ` (${position.fifoDetails.soldLots.length} Käufe)` : ''}`}
                    variant="footer"
                    expanded={true}
                    onChange={() => {}}
                  >
                    <SpaceBetween direction="vertical" size="xs">
                      {/* Steuerbehandlung Zusammenfassung */}
                      <div style={{ 
                        padding: '8px',
                        backgroundColor: '#f0f4f8',
                        border: '1px solid #cbd5e0',
                        borderRadius: '4px',
                        marginBottom: '8px'
                      }}>
                        <Box variant="small" color="text-body-secondary">
                          <strong>Steuerregelung:</strong> {
                            position.taxTreatment === 'Teilfreistellung 15%' ? '15% Freibetrag (ETF/Mischfonds)' :
                            position.taxTreatment === 'Teilfreistellung 30%' ? '30% Freibetrag (Aktienfonds)' :
                            position.taxTreatment === 'Steuerfrei Haltefrist >1 Jahr' ? 'Haltefrist-Prüfung (Einzelaktien/Krypto/Rohstoffe)' :
                            'Keine Vergünstigung (100% steuerpflichtig)'
                          }
                        </Box>
                        {position.taxTreatment === 'Steuerfrei Haltefrist >1 Jahr' && (
                          <Box variant="small" color="text-body-secondary" padding={{ top: 'xs' }}>
                            <em>Hinweis: Nur Lots mit Kaufdatum &gt; 1 Jahr sind steuerfrei</em>
                          </Box>
                        )}
                      </div>
                      
                      {/* Verkäufe Zusammenfassung */}
                      <div style={{ 
                        display: 'grid', 
                        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
                        gap: '8px',
                        fontSize: '12px'
                      }}>
                        {position.fifoDetails.soldLots.map((lot: any, index: number) => {
                          const purchaseDate = new Date(lot.date);
                          const displayDate = purchaseDate.toLocaleDateString('de-DE', { 
                            year: 'numeric', 
                            month: '2-digit',
                            day: '2-digit'
                          });
                          const oneYearAgo = new Date();
                          oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);
                          const isOlderThanOneYear = purchaseDate < oneYearAgo;
                          
                          // Use the correct effectiveTaxTreatment from the lot (already calculated with holding period check)
                          const taxableProfit = PortfolioService.applyTaxTreatment(lot.profit, lot.effectiveTaxTreatment);
                          const isTaxFree = lot.isTaxFree; // Use the pre-calculated isTaxFree from FIFO service
                          
                          return (
                            <div key={index} style={{ 
                              padding: '6px 8px',
                              backgroundColor: isTaxFree ? '#f0f8f0' : '#fafbfc',
                              border: `1px solid ${isTaxFree ? '#d4e6d4' : '#e9ecef'}`,
                              borderRadius: '4px',
                              fontSize: '11px'
                            }}>
                              <div style={{ 
                                display: 'flex', 
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                marginBottom: '2px'
                              }}>
                                <span style={{ fontWeight: '500' }}>
                                  {lot.quantity.toFixed(1)} × {formatCurrency(lot.purchasePrice, position.currency)}
                                </span>
                                <span style={{ 
                                  color: isTaxFree ? '#0b7c0b' : taxableProfit >= 0 ? '#d97706' : '#dc2626',
                                  fontWeight: '600'
                                }}>
                                  {isTaxFree ? 'Steuerfrei' : `${taxableProfit >= 0 ? '+' : ''}${formatCurrency(taxableProfit, position.currency)}`}
                                </span>
                              </div>
                              <div style={{ 
                                color: '#6b7280',
                                fontSize: '10px',
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center'
                              }}>
                                <span>Kauf: {displayDate}</span>
                                <span style={{ fontSize: '9px' }}>
                                  {lot.effectiveTaxTreatment === 'Teilfreistellung 15%' && '15% TF'}
                                  {lot.effectiveTaxTreatment === 'Teilfreistellung 30%' && '30% TF'}
                                  {lot.effectiveTaxTreatment === 'Steuerfrei Haltefrist >1 Jahr' && isOlderThanOneYear && '✓ Haltefrist'}
                                  {lot.effectiveTaxTreatment === 'keine Teilfreistellung' && position.taxTreatment === 'Steuerfrei Haltefrist >1 Jahr' && !isOlderThanOneYear && '< 1 Jahr'}
                                  {lot.effectiveTaxTreatment === 'keine Teilfreistellung' && position.taxTreatment !== 'Steuerfrei Haltefrist >1 Jahr' && 'Voll stpfl.'}
                                </span>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                      
                      {/* Gesamtzusammenfassung */}
                      <div style={{ 
                        marginTop: '8px',
                        padding: '6px 8px',
                        backgroundColor: '#fff7ed',
                        border: '1px solid #fed7aa',
                        borderRadius: '4px',
                        fontSize: '11px'
                      }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span><strong>Gesamt steuerpflichtig:</strong></span>
                          <span style={{ color: '#d97706', fontWeight: '600' }}>
                            {formatCurrency(position.steuerInfo.gewinn, position.currency)}
                          </span>
                        </div>
                        {position.fifoDetails.totalProfit !== position.steuerInfo.gewinn && (
                          <div style={{ 
                            marginTop: '2px',
                            color: '#6b7280',
                            fontSize: '10px'
                          }}>
                            Bruttogewinn: {formatCurrency(position.fifoDetails.totalProfit, position.currency)}
                          </div>
                        )}
                        {position.steuerInfo.fifoGewinnVerrechnet !== position.steuerInfo.gewinn && (
                          <div style={{
                            marginTop: '4px',
                            color: '#6b7280',
                            fontSize: '10px'
                          }}>
                            FIFO steuerpflichtig vor Pauschbetrag (Position):{' '}
                            {formatCurrency(position.steuerInfo.fifoGewinnVerrechnet, position.currency)}
                          </div>
                        )}
                      </div>
                    </SpaceBetween>
                  </ExpandableSection>
                </SpaceBetween>
              </Container>
            ))}
          </SpaceBetween>
        </Container>
      ))}

      {/* Sparer-Pauschbetrag Verlauf */}
      {sparerPauschbetragEnabled && verkaufsanweisungenNachPortfolio.allowanceTracking.length > 0 && (
        <Container
          header={
            <Header variant="h3">
              Sparer-Pauschbetrag Verwendung
            </Header>
          }
        >
          <Box variant="p" color="text-body-secondary" padding={{ bottom: 's' }}>
            Schrittweise Anwendung des Sparer-Pauschbetrags in der Reihenfolge der Aufgaben:
          </Box>
          
          <SpaceBetween direction="vertical" size="s">
            {verkaufsanweisungenNachPortfolio.allowanceTracking.map((tracking, index) => (
              <div key={index} style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '12px',
                backgroundColor: '#f8f9fa',
                borderRadius: '6px'
              }}>
                <div>
                  <Box variant="span">
                    <strong>{index + 1}.</strong> {tracking.positionName}
                  </Box>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <Box variant="span" color={tracking.verwendeter > 0 ? "text-status-success" : "text-body-secondary"}>
                    {tracking.verwendeter > 0 
                      ? `-${formatCurrency(tracking.verwendeter)}`
                      : '€0.00'
                    }
                  </Box>
                  <br />
                  <Box variant="small" color="text-body-secondary">
                    Verbleibt: {formatCurrency(tracking.verbleibend)}
                  </Box>
                </div>
              </div>
            ))}
          </SpaceBetween>
        </Container>
      )}

      {/* Saubere Hinweise */}
      <Container
        header={
          <Header variant="h3">
            Wichtige Hinweise
          </Header>
        }
      >
        <ul style={{ marginLeft: '20px' }}>
          <li>Die angezeigte Reihenfolge zeigt ein mögliches Ergebnis der Simulation und dient ausschließlich der Veranschaulichung. Sie stellt keine Empfehlung zur tatsächlichen Umsetzung dar</li>
          <li>Die Steuerberechnung basiert auf FIFO-Prinzip und aktuellen Kursen</li>
          <li>Tatsächliche Verkaufspreise können von den berechneten Werten abweichen</li>
          <li>Bei Fragen zur Steuerbehandlung konsultieren Sie Ihren Steuerberater</li>
          <li>Bewahren Sie alle Verkaufsbelege für die Steuererklärung auf</li>
        </ul>
      </Container>
    </SpaceBetween>
  );
};

export default VerkaufsanweisungenTab;